import { Component } from 'react';

/**
 * Catches any rendering error in child components and shows a friendly
 * fallback instead of a blank/black screen. Logs to console for debugging.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] caught:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="min-h-screen flex items-center justify-center p-6 bg-zinc-950"
          data-testid="error-boundary-fallback"
        >
          <div className="max-w-md text-center bg-white/[0.04] border border-white/10 rounded-3xl p-8">
            <div className="text-5xl mb-4">⚠️</div>
            <h1 className="text-2xl font-bold text-white mb-2">Something went wrong</h1>
            <p className="text-sm text-zinc-400 mb-6">
              We hit a snag rendering this page. The team has been notified.
            </p>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => { this.setState({ hasError: false, error: null }); window.location.reload(); }}
                className="w-full px-5 py-3 rounded-xl bg-white text-black font-semibold text-sm hover:bg-zinc-200 transition-colors"
                data-testid="error-boundary-reload"
              >
                Reload page
              </button>
              <button
                onClick={() => { window.location.href = '/dashboard'; }}
                className="w-full px-5 py-3 rounded-xl bg-transparent border border-white/10 text-zinc-400 text-sm hover:text-white hover:border-white/20 transition-colors"
                data-testid="error-boundary-home"
              >
                Back to dashboard
              </button>
            </div>
            {this.state.error?.message && (
              <p className="text-[10px] text-zinc-600 mt-4 font-mono break-all">
                {String(this.state.error.message).slice(0, 200)}
              </p>
            )}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
